import { useEffect, useState } from 'react'
import {
  Link as RouterLink,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material'
import {
  IconExternalLink,
  IconLayoutDashboard,
  IconLogout,
  IconSpeakerphone,
  IconSquarePlus,
} from '@tabler/icons-react'
import { tokens } from '@/theme'
import { apiUrl } from '@/lib/apiBase'
import { clearTenantToken, getTenantToken } from '@/lib/tenantAdminApi'
import NotFound from '@/pages/NotFound'
import type { TenantAdminContext } from '@/pages/tenant-admin/context'

interface TenantBranding {
  slug: string
  display_name: string | null
  gsa: { name: string }
}

/**
 * Authenticated shell for the GSA self-serve portal (ag-009 screens 2–4).
 *
 * Thin authoring layer over the public /g/<slug> page: publishing here updates
 * the public page directly — there is no separate "site build". Unauthenticated
 * visits bounce to the slug's login screen.
 */
export default function TenantAdminShell() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [branding, setBranding] = useState<TenantBranding | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const authed = slug ? getTenantToken(slug) !== null : false

  useEffect(() => {
    if (!slug || !authed) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    fetch(apiUrl(`/api/agro/v1/g/${encodeURIComponent(slug)}`), { signal: controller.signal })
      .then((res) => (res.ok ? (res.json() as Promise<TenantBranding>) : Promise.reject(res.status)))
      .then((data) => setBranding(data))
      .catch((err) => {
        if ((err as { name?: string })?.name === 'AbortError') return
        setNotFound(true)
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [slug, authed])

  if (!slug) return <NotFound />
  if (!authed) return <Navigate to={`/g/${slug}/admin/login`} replace />
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 16 }}>
        <CircularProgress size={36} sx={{ color: tokens.green.main }} />
      </Box>
    )
  }
  if (notFound || !branding) return <NotFound />

  const displayName = branding.display_name?.trim() || branding.gsa.name

  const navItems = [
    { to: `/g/${slug}/admin`, label: 'Dashboard', icon: <IconLayoutDashboard size={17} />, exact: true },
    { to: `/g/${slug}/admin/meetings/new`, label: 'Post a meeting', icon: <IconSquarePlus size={17} /> },
    { to: `/g/${slug}/admin/announcements`, label: 'Announcements', icon: <IconSpeakerphone size={17} /> },
  ]

  const signOut = () => {
    clearTenantToken(slug)
    navigate(`/g/${slug}`)
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: tokens.cream }}>
      {/* ── Admin header ─────────────────────────────────────────────────── */}
      <Box sx={{ background: tokens.green.dark, color: '#fff', py: 2.5 }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between' }}
          >
            <Box>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.65)', display: 'block' }}>
                GSA Portal Admin
              </Typography>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                {displayName}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                component={RouterLink}
                to={`/g/${slug}`}
                size="small"
                endIcon={<IconExternalLink size={15} />}
                sx={{ color: 'rgba(255,255,255,0.85)' }}
              >
                View public page
              </Button>
              <Button
                size="small"
                onClick={signOut}
                endIcon={<IconLogout size={15} />}
                sx={{ color: 'rgba(255,255,255,0.85)' }}
              >
                Sign out
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* ── Nav ──────────────────────────────────────────────────────────── */}
      <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${tokens.green[100]}` }}>
        <Container maxWidth="lg">
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', py: 1 }}>
            {navItems.map((item) => {
              const active = item.exact ? pathname === item.to : pathname.startsWith(item.to)
              return (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  size="small"
                  startIcon={item.icon}
                  sx={{
                    px: 2,
                    color: active ? tokens.green.main : 'text.secondary',
                    bgcolor: active ? tokens.green[50] : 'transparent',
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {item.label}
                </Button>
              )
            })}
          </Stack>
        </Container>
      </Box>

      {/* ── Screen body ──────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Outlet context={{ slug, displayName } satisfies TenantAdminContext} />
      </Container>

      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Powered by AgroLense · changes publish to your public page immediately
        </Typography>
      </Box>
    </Box>
  )
}
